/**
 * 필드 표시 규칙 정의
 * INPUT-FIELDS-MATRIX.md의 규칙을 코드로 매핑
 * 
 * 각 필드의 표시 여부와 필수 여부를 결정하는 규칙을 중앙화하여 관리합니다.
 */

/**
 * 필드 표시 규칙 정의
 * @typedef {Object} FieldDisplayRule
 * @property {boolean|function} show - 필드 표시 여부 (함수인 경우 context를 받아 boolean 반환)
 * @property {boolean|function} required - 필드 필수 여부 (함수인 경우 context를 받아 boolean 반환)
 */

/**
 * 필드 표시 규칙 객체
 * @type {Object<string, FieldDisplayRule>}
 */
export const FIELD_DISPLAY_RULES = {
    // ========== 기본요금 관련 필드 ==========
    
    /**
     * 기본요금 값
     * - 표시: baseFeeCalcType이 None이 아닐 때
     * - 필수: Fixed, Contract, Demand, PeakLinked일 때
     */
    baseFeeValue: {
        show: (context) => {
            const { baseFeeCalcType } = context;
            return baseFeeCalcType && baseFeeCalcType !== 'None';
        },
        required: (context) => {
            const { baseFeeCalcType } = context;
            return ['Fixed', 'Contract', 'Demand', 'PeakLinked'].includes(baseFeeCalcType);
        }
    },
    
    /**
     * 계약 용량
     * - 표시: Contract, Demand, PeakLinked일 때
     * - 필수: Contract, Demand, PeakLinked일 때
     */
    contractCapacity: {
        show: (context) => {
            const { baseFeeCalcType } = context;
            return ['Contract', 'Demand', 'PeakLinked'].includes(baseFeeCalcType);
        },
        required: (context) => {
            const { baseFeeCalcType } = context;
            return ['Contract', 'Demand', 'PeakLinked'].includes(baseFeeCalcType);
        }
    },
    
    /**
     * 최대 수요 제한
     * - 표시: Demand 또는 PeakLinked일 때 (단, GAS이고 PeakLinked이면 표시 안 함)
     * - 필수: 없음
     */
    maxDemandLimit: {
        show: (context) => {
            const { baseFeeCalcType, energySourceCode } = context;
            if (baseFeeCalcType !== 'Demand' && baseFeeCalcType !== 'PeakLinked') return false;
            if (energySourceCode === 'GAS' && baseFeeCalcType === 'PeakLinked') return false;
            return true;
        },
        required: false
    },
    
    /**
     * 한전 고객번호
     * - 표시: Demand 또는 PeakLinked일 때 (단, GAS이면 표시 안 함)
     * - 필수: 없음
     */
    kepcoCustNo: {
        show: (context) => {
            const { baseFeeCalcType, energySourceCode } = context;
            if (baseFeeCalcType !== 'Demand' && baseFeeCalcType !== 'PeakLinked') return false;
            if (energySourceCode === 'GAS') return false;
            return true;
        },
        required: false
    },
    
    /**
     * 피크 최대수요전력
     * - 표시: PeakLinked이고 GAS가 아닐 때
     * - 필수: 없음
     */
    applyPeak: {
        show: (context) => {
            const { baseFeeCalcType, energySourceCode } = context;
            return baseFeeCalcType === 'PeakLinked' && energySourceCode !== 'GAS';
        },
        required: false
    },
    
    // ========== 사용량 요금 관련 필드 ==========
    
    /**
     * 단일 요금제 단가
     * - 표시: hasFlatCost === 1일 때
     * - 필수: hasFlatCost === 1일 때
     */
    flatCostFee: {
        show: (context) => {
            return context.hasFlatCost === 1;
        },
        required: (context) => {
            return context.hasFlatCost === 1;
        }
    },
    
    /**
     * 보정계수 (GAS만)
     * - 표시: energySourceCode === 'GAS'일 때
     * - 필수: 없음
     */
    correctionFactor: {
        show: (context) => {
            return context.energySourceCode === 'GAS';
        },
        required: false
    },
    
    /**
     * 환산계수 (GAS만)
     * - 표시: energySourceCode === 'GAS'일 때
     * - 필수: 없음
     */
    conversionFactor: {
        show: (context) => {
            return context.energySourceCode === 'GAS';
        },
        required: false
    },
    
    // ========== cost_detail 관련 필드 ==========
    
    /**
     * 사용량 구간 시작 (누진 요금제)
     * - 표시: hasProgressive === 1일 때
     * - 필수: hasProgressive === 1일 때
     */
    usageFrom: {
        show: (context) => {
            return context.hasProgressive === 1;
        },
        required: (context) => {
            return context.hasProgressive === 1;
        }
    },
    
    /**
     * 사용량 구간 종료 (누진 요금제)
     * - 표시: hasProgressive === 1일 때
     * - 필수: hasProgressive === 1일 때 (단, 마지막 구간은 null 허용)
     */
    usageTo: {
        show: (context) => {
            return context.hasProgressive === 1;
        },
        required: (context) => {
            // 마지막 구간은 usageTo가 null일 수 있으므로 항상 필수는 아님
            // 하지만 검증 로직에서 마지막 구간이 아닌 경우 필수로 처리
            return context.hasProgressive === 1;
        }
    },
    
    /**
     * 시간대 코드 (시간대별 요금제)
     * - 표시: hasTimeSlot === 1일 때
     * - 필수: hasTimeSlot === 1일 때
     */
    timeSlotCode: {
        show: (context) => {
            return context.hasTimeSlot === 1;
        },
        required: (context) => {
            return context.hasTimeSlot === 1;
        }
    },
    
    /**
     * 계절 코드 (계절별 요금제)
     * - 표시: hasSeason === 1일 때
     * - 필수: hasSeason === 1일 때
     */
    seasonCode: {
        show: (context) => {
            return context.hasSeason === 1;
        },
        required: (context) => {
            return context.hasSeason === 1;
        }
    },
    
    /**
     * 요일 타입 (요일별 요금제)
     * - 표시: hasDayType === 1일 때
     * - 필수: hasDayType === 1일 때
     */
    dayType: {
        show: (context) => {
            return context.hasDayType === 1;
        },
        required: (context) => {
            return context.hasDayType === 1;
        }
    },
};

